import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Drawer,
  Paper,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UserCenter from './UserCenter';
import apiClient from '../utils/apiInterceptor';
import axios from 'axios';
import apiService from '../services/api';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PeopleIcon from '@mui/icons-material/People';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SessionExpiredDialog from './SessionExpiredDialog';
import { WechatGuideBanner } from './WechatGuide';
import './Dashboard.css';

// 科技蓝青配色方案 - 与登录界面一致
const COLORS = {
  primary: '#3b82f6',      // 蓝色
  secondary: '#06b6d4',    // 青色
  accent: '#06b6d4',        // 绿色
  light: '#60a5fa',        // 浅蓝
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  bgDark: '#0a0f1a',
  bgCard: 'rgba(15, 23, 42, 0.98)',
  glow: 'rgba(59, 130, 246, 0.35)',
  glowGreen: 'rgba(6, 182, 212, 0.35)',
  border: 'rgba(59, 130, 246, 0.15)',
  // 辅助写作次数颜色 - 使用青色
  assistColor: '#06b6d4',
  assistGlow: 'rgba(6, 182, 212, 0.35)',
};

const glowShadowMap = {
  '#00ff9f': 'rgba(0, 255, 159, 0.85)',
  '#66ffcc': 'rgba(102, 255, 204, 0.75)',
  '#ffd700': 'rgba(255, 215, 0, 0.8)',
  '#ffe082': 'rgba(255, 224, 130, 0.7)',
  '#ff6b6b': 'rgba(255, 107, 107, 0.8)',
  '#ff8a80': 'rgba(255, 138, 128, 0.7)',
  '#64b5f6': 'rgba(100, 181, 246, 0.75)',
  '#90caf9': 'rgba(144, 202, 249, 0.7)',
  '#ba68c8': 'rgba(186, 104, 200, 0.75)',
  '#ce93d8': 'rgba(206, 147, 216, 0.7)',
  '#00e5ff': 'rgba(0, 229, 255, 0.75)',
  '#ffffff': 'rgba(255, 255, 255, 0.8)'
};

const announcementGlowStyles = Object.entries(glowShadowMap).reduce((acc, [color, shadow]) => {
  const lower = color.toLowerCase();
  acc[`& [style*="color: ${color}"], & [style*="color: ${lower}"]`] = {
    textShadow: `0 0 10px ${shadow}`,
    fontWeight: 600
  };
  return acc;
}, {});

const resolveGlowShadow = (color, radius = '12px') => {
  if (!color) return 'none';
  const normalized = color.toLowerCase();
  const base = glowShadowMap[normalized] || glowShadowMap[color] || `${normalized}b3`;
  return `0 0 ${radius} ${base}`;
};

// Alert组件
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// 获取当前环境和主机名
const isDevelopment = process.env.NODE_ENV === 'development';
const currentHost = window.location.hostname;

// 根据环境和主机名决定API基础URL
const getApiBaseUrl = () => {
  // 如果通过环境变量明确设置了API URL，则使用它
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 开发环境或本地访问，跨域调用本地后端
  if (isDevelopment || currentHost === 'localhost') {
    return 'http://localhost:5000/api';
  }

  // 生产环境：同源相对路径，自动适配任何域名/IP/协议（nginx 统一代理 /api 到后端）
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// 星空背景动画组件
const StarField = React.memo(() => {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const shootingStarsRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    starsRef.current = [];
    for (let i = 0; i < 150; i++) {
      starsRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.8 + 0.2,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
      });
    }

    const createShootingStar = () => {
      if (shootingStarsRef.current.length < 3 && Math.random() < 0.015) {
        shootingStarsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height * 0.5,
          length: Math.random() * 80 + 40,
          speed: Math.random() * 8 + 6,
          angle: Math.PI / 4 + Math.random() * 0.3,
          opacity: 1,
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 15, 26, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      starsRef.current.forEach(star => {
        star.twinkle += star.twinkleSpeed;
        const baseOpacity = 0.3 + star.opacity * 0.7 * (0.5 + 0.5 * Math.sin(star.twinkle));

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(165, 180, 252, ${baseOpacity})`;
        ctx.fill();

        if (star.size > 1.5) {
          const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.size * 4);
          gradient.addColorStop(0, `rgba(96, 165, 250, ${baseOpacity * 0.3})`);
          gradient.addColorStop(1, 'transparent');
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 4, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();
        }
      });

      createShootingStar();

      shootingStarsRef.current = shootingStarsRef.current.filter(ss => {
        ss.x += Math.cos(ss.angle) * ss.speed;
        ss.y += Math.sin(ss.angle) * ss.speed;
        ss.opacity -= 0.012;

        if (ss.opacity <= 0) return false;

        const tailX = ss.x - Math.cos(ss.angle) * ss.length;
        const tailY = ss.y - Math.sin(ss.angle) * ss.length;

        const gradient = ctx.createLinearGradient(tailX, tailY, ss.x, ss.y);
        gradient.addColorStop(0, 'transparent');
        gradient.addColorStop(1, `rgba(59, 130, 246, ${ss.opacity})`);

        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(ss.x, ss.y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(ss.x, ss.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${ss.opacity})`;
        ctx.fill();

        return true;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resize();
      starsRef.current.forEach(star => {
        if (star.x > canvas.width) star.x = Math.random() * canvas.width;
        if (star.y > canvas.height) star.y = Math.random() * canvas.height;
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
});

// 神经网络动画组件
const NeuralNetwork = React.memo(() => {
  const canvasRef = useRef(null);
  const nodesRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const nodeCount = Math.min(25, Math.floor(window.innerWidth / 80));
    nodesRef.current = [];

    for (let i = 0; i < nodeCount; i++) {
      nodesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 3 + 2,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodesRef.current.forEach((node, i) => {
        node.pulse += 0.03;
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        const glowSize = node.size * (2 + Math.sin(node.pulse) * 0.5);
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowSize * 3);
        gradient.addColorStop(0, `rgba(59, 130, 246, ${0.4 + Math.sin(node.pulse) * 0.2})`);
        gradient.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowSize * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96, 165, 250, ${0.6 + Math.sin(node.pulse) * 0.4})`;
        ctx.fill();

        nodesRef.current.slice(i + 1).forEach(node2 => {
          const dx = node.x - node2.x;
          const dy = node.y - node2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 200) {
            const opacity = (1 - dist / 200) * 0.3;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(node2.x, node2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: 0.6,
      }}
    />
  );
});

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  // <600px 移动端
  const isVeryNarrow = useMediaQuery('(max-width:425px)');     // ≤425px 超窄屏
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('md')); // <960px 窄屏
  const isUltraNarrow = useMediaQuery('(max-width:374px)');    // ≤374px 极窄屏(iPhone SE)

  const [novels, setNovels] = useState([]);
  const [novelLoadError, setNovelLoadError] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newNovel, setNewNovel] = useState({
    title: '',
    description: '',
  });
  // 添加刷新触发器状态
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // 🔥 编辑备注信息的状态
  const [editingNovelId, setEditingNovelId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');
  
  // 创建小说状态
  const [creatingNovel, setCreatingNovel] = useState(false);
  
  // 用户相关状态
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userCenterOpen, setUserCenterOpen] = useState(false);
  const [userCenterInitialTab, setUserCenterInitialTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 添加确认对话框的状态
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    novelId: null
  });

  const [announcement, setAnnouncement] = useState(null);

  // 辅助创作次数状态
  const [assistQuota, setAssistQuota] = useState({ remaining: 50, dailyLimit: 50, usedToday: 0 });

  // 添加强制刷新用户数据的函数
  const forceRefreshUserData = useCallback(async () => {
    setUserLoading(true);
    try {
      // 只用API拉取数据
      const response = await apiService.getUserInfo();
      if (response && response.data) {
        setUserInfo(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } else {
        // API拉不到再兜底用localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUserInfo(userData);
        } else {
          setUserInfo(null);
        }
      }
    } catch (error) {
      console.error('强制刷新用户数据失败:', error);
      // 兜底用localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUserInfo(userData);
      } else {
        setUserInfo(null);
      }
    } finally {
      setUserLoading(false);
    }
  }, []);

  // 获取辅助创作次数
  const fetchAssistQuota = useCallback(async () => {
    try {
      const res = await apiClient.get('/assist/ai/quota');
      if (res.data && res.data.success) {
        const body = res.data; // 后端直接返回 { success, usedToday, remaining, dailyLimit }，不在 data 字段里
        const remaining = typeof body.remaining === 'number' ? body.remaining : 50;
        setAssistQuota({
          remaining,
          dailyLimit: body.dailyLimit || 50,
          usedToday: body.usedToday ?? (50 - remaining),
        });
      }
    } catch (e) {
      console.error('获取辅助创作次数失败:', e);
    }
  }, []);

  // 定义loadNovels函数，使用useCallback确保其稳定性
  // 优化：只加载小说列表，不重复获取用户信息
  // 修复：添加自动重试机制（最多2次），确保项目列表必须加载出来
  const loadNovels = useCallback(async (skipLoadingState = false, retryCount = 0) => {
    if (!skipLoadingState) {
      setLoading(true);
    }
    setNovelLoadError('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('未找到认证令牌，请重新登录');
      }
      // 只获取小说列表，用户信息由forceRefreshUserData单独处理
      const response = await apiClient.get('/novels/user/me');

      // 区分真实空数据和错误响应
      if (response.data && response.data.success === false) {
        // 后端明确返回失败，尝试重试
        if (retryCount < 2) {
          console.warn(`[DEBUG] Dashboard: 第${retryCount + 1}次加载失败，${1000 * (retryCount + 1)}ms后重试...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return loadNovels(skipLoadingState, retryCount + 1);
        } else {
          throw new Error(response.data.message || '加载项目列表失败，请稍后重试');
        }
      }

      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        setNovels(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setNovels(response.data);
      } else {
        // 数据格式异常，视为空（首次加载才报错）
        if (!skipLoadingState && retryCount === 0) {
          setNovels([]);
        } else {
          // 重试后仍为空，保持原数据不覆盖
        }
      }
    } catch (error) {
      console.error('[DEBUG] Dashboard: 加载小说列表时出错', error);
      const friendlyMsg = error.userMessage || error.message || '加载项目列表失败，请检查网络后重试';
      if (retryCount < 2) {
        console.warn(`[DEBUG] Dashboard: 第${retryCount + 1}次加载失败，${2000 * (retryCount + 1)}ms后重试...`);
        setIsRetrying(true);
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
        setIsRetrying(false);
        return loadNovels(skipLoadingState, retryCount + 1);
      }
      // 最多重试2次后仍失败，显示错误但保留原数据
      setNovelLoadError(friendlyMsg);
      // 不要覆盖已有数据！只在首次加载时设置为空
      if (!skipLoadingState && retryCount === 0) {
        setNovels([]);
      }
    } finally {
      if (!skipLoadingState) {
        setLoading(false);
        setIsRetrying(false);
      }
    }
  }, []);

  // 监听全局配额刷新事件
  useEffect(() => {
    const onRefresh = () => fetchAssistQuota();
    window.addEventListener('assistAiQuotaRefresh', onRefresh);
    return () => window.removeEventListener('assistAiQuotaRefresh', onRefresh);
  }, [fetchAssistQuota]);

  // 在组件挂载时调用
  useEffect(() => {
    // 并行加载用户信息、小说列表和辅助创作次数，提高加载速度
    Promise.all([
      forceRefreshUserData(),
      loadNovels(),
      fetchAssistQuota()
    ]).catch(error => {
      console.error('[DEBUG] Dashboard: 初始化加载失败', error);
    });
  }, [forceRefreshUserData, loadNovels, fetchAssistQuota]);
  
  // 当refreshTrigger变化时，只刷新小说列表（不重新加载用户信息）
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadNovels(true); // skipLoadingState=true，避免闪烁
    }
  }, [refreshTrigger, loadNovels]);
  
  // 事件监听只触发刷新，不直接setUserInfo
  useEffect(() => {
    const handleUserDataUpdate = (event) => {
      // 直接使用event.detail中的数据
      const updateSource = event.detail && event.detail.source;
      
      console.log('收到userDataUpdated事件，来源:', updateSource);
      
      // 如果是充值操作触发的更新，我们只需更新余额，不需要完全刷新
      if (updateSource === 'recharge' && event.detail && event.detail.userData) {
        // 直接更新用户信息中的余额
        setUserInfo(prevUserInfo => {
          if (!prevUserInfo) return event.detail.userData;
          
          return {
            ...prevUserInfo,
            balance: event.detail.userData.balance
          };
        });
        return;
      }
      
      // 对于其他来源的更新，使用常规的强制刷新
      forceRefreshUserData();
    };
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, [forceRefreshUserData]);

  useEffect(() => {
    // 监听充值和扣费余额更新事件
    const handleBalanceUpdate = (event) => {
      console.log('收到余额更新事件:', event.detail);
      
      // 更新用户信息中的余额
      if (userInfo) {
        const updatedUserInfo = {
          ...userInfo,
          balance: event.detail.newBalance
        };
        
        console.log('Dashboard更新用户信息:', updatedUserInfo);
        setUserInfo(updatedUserInfo);
        
        // 更新本地存储
        localStorage.setItem('user', JSON.stringify(updatedUserInfo));
        
        // 根据事件类型显示不同通知
        if (event.detail.type === 'recharge') {
          // 充值成功通知
          setAlert({
            open: true,
            message: `充值成功! 已添加 ${event.detail.amount} 余额`,
            severity: 'success'
          });
        } else if (event.detail.type === 'consume') {
          // 扣费成功通知（可选，不显示以避免频繁打扰）
          console.log(`扣费成功，消耗 ${event.detail.amount} 字，当前余额: ${event.detail.newBalance}`);
        }
      }
    };
    
    // 注册事件监听器
    window.addEventListener('balanceUpdated', handleBalanceUpdate);
    
    // 组件卸载时清理
    return () => {
      window.removeEventListener('balanceUpdated', handleBalanceUpdate);
    };
  }, [userInfo]);

  const handleCreateNovel = async () => {
    console.log('[DEBUG] Dashboard: 开始创建新小说');
    setCreatingNovel(true);
    
    try {
      // 验证令牌
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('[DEBUG] Dashboard: 创建小说失败 - 未找到认证令牌');
        setAlert({
              open: true,
          message: '创建小说失败：未找到认证令牌，请重新登录',
          severity: 'error'
        });
        return;
      }
      
      // 准备新小说数据
      const newNovelData = {
        title: newNovel.title || '未命名小说',
        description: newNovel.description || '',
        settings: {
          genre: '',
          writingStyle: '',
          characters: [],
          worldBuilding: '',
          plotPoints: [],
          themes: []
        },
        chapters: [] // 不创建默认章节，保持为空数组
      };
      
      console.log('[DEBUG] Dashboard: 准备发送的新小说数据:', newNovelData);
      
      // 发送创建请求到服务器
      const response = await apiClient.post('/novels', newNovelData);
      
      console.log('[DEBUG] Dashboard: 创建小说响应状态:', response.status);
      console.log('[DEBUG] Dashboard: 创建小说响应:', response.data);
      console.log('[DEBUG] Dashboard: 响应数据类型:', typeof response.data);
      console.log('[DEBUG] Dashboard: 响应数据属性:', Object.keys(response.data));
      
      // 判断响应是否有效
      if (!response.data) {
        throw new Error('服务器返回了空的响应数据');
      }
      
      // 提取创建的小说数据 - 处理不同可能的响应格式
      let createdNovel;
      
      if (response.data.success && response.data.data) {
        // 新格式: { success: true, data: 小说数据 }
        console.log('[DEBUG] Dashboard: 检测到新的响应格式，数据在data字段中');
        createdNovel = response.data.data;
      } else if (response.data.data) {
        console.log('[DEBUG] Dashboard: 使用response.data.data作为创建的小说');
        createdNovel = response.data.data;
      } else if (response.data.novel) {
        console.log('[DEBUG] Dashboard: 使用response.data.novel作为创建的小说');
        createdNovel = response.data.novel;
      } else {
        console.log('[DEBUG] Dashboard: 使用response.data作为创建的小说');
        createdNovel = response.data;
      }
      
      console.log('[DEBUG] Dashboard: 提取的小说数据:', createdNovel);
      
      // 确保获取到ID - 尝试多种可能的ID字段
      const novelId = createdNovel._id || createdNovel.id || 
                     (createdNovel.data && (createdNovel.data._id || createdNovel.data.id));
      
      if (!novelId) {
        console.error('[DEBUG] Dashboard: 未能获取小说ID，返回的数据:', createdNovel);
        console.error('[DEBUG] Dashboard: 完整响应:', response.data);
        
        // 生成临时ID，确保至少可以显示在列表中
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        console.log(`[DEBUG] Dashboard: 生成临时ID: ${tempId}`);
        
        // 保存临时ID到本地存储
        localStorage.setItem('lastCreatedNovelId', tempId);
        
        // 添加会话标记，表示这个小说是从Dashboard创建的
        sessionStorage.setItem('createdFromDashboard', 'true');
        
        // 标准化小说数据
        const standardizedNovel = {
          id: tempId,
          title: newNovel.title || '未命名小说',
          description: newNovel.description || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: '进行中',
          coverImage: '',
          isTemporary: true
        };
        
        // 更新小说列表
        setNovels(prevNovels => [standardizedNovel, ...prevNovels]);
        
        // 关闭对话框并重置表单
        setOpenDialog(false);
        setNewNovel({ title: '', description: '' });
        
        // 显示警告消息
        setAlert({
          open: true,
          message: '小说已创建，但无法获取ID，将使用临时ID',
          severity: 'warning'
        });
        
        console.log('[DEBUG] Dashboard: 跳转到编辑页面:', `/novel-editor/${tempId}`);
        navigate(`/novel-editor/${tempId}`);
        return;
      }
      
      // 保存ID到本地存储 - 这对于在NovelEditor中恢复ID很重要
      localStorage.setItem('lastCreatedNovelId', novelId);
      
      // 添加会话标记，表示这个小说是从Dashboard创建的
      sessionStorage.setItem('createdFromDashboard', 'true');
      
      // 标准化小说数据
      const standardizedNovel = {
        id: novelId,
        title: createdNovel.title || newNovel.title || '无标题',
        description: createdNovel.description || newNovel.description || '',
        createdAt: createdNovel.createdAt || new Date().toISOString(),
        updatedAt: createdNovel.updatedAt || createdNovel.createdAt || new Date().toISOString(),
        status: createdNovel.status || '进行中',
        coverImage: createdNovel.coverImage || ''
      };
      
      console.log('[DEBUG] Dashboard: 标准化后的新小说:', standardizedNovel);
      console.log('[DEBUG] Dashboard: 新小说ID:', novelId);
      
      // 更新小说列表 - 添加到列表开头
      setNovels(prevNovels => [standardizedNovel, ...prevNovels]);
      
      // 关闭对话框并重置表单
      setOpenDialog(false);
      setNewNovel({ title: '', description: '' });
      
      // 显示成功消息
      setAlert({
        open: true,
        message: '小说创建成功',
        severity: 'success'
      });
      
      // 跳转到编辑页面
      console.log('[DEBUG] Dashboard: 跳转到编辑页面:', `/novel-editor/${novelId}`);
      navigate(`/novel-editor/${novelId}`);
    } catch (error) {
      console.error('[DEBUG] Dashboard: 创建小说失败:', error);
      
      // 详细记录错误信息
      console.error('[DEBUG] Dashboard: 错误信息:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      let errorMessage = '创建小说失败，请重试';
      
      if (error.response) {
        console.error('[DEBUG] Dashboard: 服务器错误响应:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        
        if (error.response.data && error.response.data.message) {
          errorMessage = `创建小说失败: ${error.response.data.message}`;
        } else if (error.response.status === 401) {
          errorMessage = '认证已过期，请重新登录';
          localStorage.removeItem('authToken'); // 清除过期的token
          setTimeout(() => navigate('/login'), 2000);
        }
      } else if (error.request) {
        // 请求已发送但未收到响应
        console.error('[DEBUG] Dashboard: 未收到服务器响应:', error.request);
        errorMessage = '服务器无响应，请检查网络连接';
      }
      
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setCreatingNovel(false);
    }
  };

  // 处理删除确认对话框
  const handleOpenDeleteDialog = (id) => {
    setDeleteConfirmDialog({
      open: true,
      novelId: id
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      novelId: null
    });
  };

  const handleConfirmDelete = async () => {
    const id = deleteConfirmDialog.novelId;
    try {
      // 验证令牌
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('[DEBUG] Dashboard: 删除小说失败 - 未找到认证令牌');
        setAlert({
          open: true,
          message: '删除小说失败：未找到认证令牌，请重新登录',
          severity: 'error'
        });
        return;
      }
      
      console.log(`[DEBUG] Dashboard: 正在删除小说 ID=${id}`);
      
      // 发送删除请求到服务器
      const response = await axios.delete(
        `${API_BASE_URL}/novels/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[DEBUG] Dashboard: 删除小说响应:', response.data);
      
      // 优化：直接从本地状态中移除小说，避免重新加载整个列表
      // 支持多种ID格式（_id, id）
      setNovels(prevNovels => 
        prevNovels.filter((novel) => {
          const novelId = novel._id || novel.id;
          return novelId !== id;
        })
      );
      
      // 不再触发完整刷新，直接更新状态即可
      // setRefreshTrigger(prev => prev + 1); // 已优化：删除后不重新加载
      
      setAlert({
        open: true,
        message: '小说删除成功',
        severity: 'success'
      });
    } catch (error) {
      console.error('[DEBUG] Dashboard: 删除小说失败:', error);
      
      let errorMessage = '删除小说失败，请重试';
      
      if (error.response) {
        console.error('[DEBUG] Dashboard: 服务器错误响应:', error.response.status, error.response.data);
        
        if (error.response.data && error.response.data.message) {
          errorMessage = `删除小说失败: ${error.response.data.message}`;
        } else if (error.response.status === 401) {
          errorMessage = '认证已过期，请重新登录';
          setTimeout(() => navigate('/login'), 2000);
        }
      }
      
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleDeleteNovel = (id) => {
    handleOpenDeleteDialog(id);
  };
  
  // 🔥 开始编辑备注
  const handleStartEditDescription = (novel) => {
    setEditingNovelId(novel._id);
    setEditingDescription(novel.description || '');
  };
  
  // 🔥 保存备注
  const handleSaveDescription = async (novelId) => {
    try {
      // 限制字数为20个字
      const trimmedDescription = editingDescription.trim().slice(0, 20);
      
      const response = await apiClient.put(`/novels/${novelId}`, {
        description: trimmedDescription
      });
      
      if (response.data.success) {
        // 更新本地状态
        setNovels(prevNovels => 
          prevNovels.map(novel => 
            novel._id === novelId 
              ? { ...novel, description: trimmedDescription }
              : novel
          )
        );
        
        setAlert({
          open: true,
          message: '备注信息已更新',
          severity: 'success'
        });
      }
      
      // 退出编辑状态
      setEditingNovelId(null);
      setEditingDescription('');
    } catch (error) {
      console.error('更新备注失败:', error);
      setAlert({
        open: true,
        message: '更新备注失败，请重试',
        severity: 'error'
      });
    }
  };
  
  // 🔥 取消编辑备注
  const handleCancelEditDescription = () => {
    setEditingNovelId(null);
    setEditingDescription('');
  };
  
  // 用户菜单处理函数
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  const handleUserCenterOpen = (initialTab = 0) => {
    setUserCenterInitialTab(initialTab);
    setUserCenterOpen(true);
    handleUserMenuClose();
  };
  
  const handleUserCenterClose = () => {
    setUserCenterOpen(false);
    
    // 重新获取用户信息，以便更新余额
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUserInfo(JSON.parse(savedUser));
    }
  };
  const handleLogout = async () => {
    try {
      // 调用API登出接口
      await apiService.logout();
      console.log('用户已成功登出');
    } catch (error) {
      console.error('登出失败:', error);
      // 即使API调用失败，也要清除本地存储
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('sessionId');
    }
    
    navigate('/login');
    handleUserMenuClose();
  };

  // 获取最新公告
  const fetchLatestAnnouncement = async () => {
    try {
      const response = await apiClient.get('/auth/announcement');
      if (response.data && response.data.success && response.data.announcement) {
        const announcement = response.data.announcement;
        // 只有当公告存在且不是"暂无公告"时才显示
        if (announcement.content && announcement.content !== '暂无公告') {
          const normalizedGlowColor =
            typeof announcement.glowColor === 'string'
              ? announcement.glowColor.toLowerCase()
              : '';
          setAnnouncement({
            ...announcement,
            glowColor: normalizedGlowColor
          });
        } else {
          setAnnouncement(null);
        }
      } else {
        setAnnouncement(null);
      }
    } catch (error) {
      console.error('获取公告失败:', error);
      setAnnouncement(null);
    }
  };

  useEffect(() => {
    fetchLatestAnnouncement();
  }, []);

  // 监听会话过期事件
  const [sessionExpiredDialogOpen, setSessionExpiredDialogOpen] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => {
      setSessionExpiredDialogOpen(true);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  return (
    <Box className="dashboard-container">
      {/* 星空背景动画 */}
      <StarField />
      <NeuralNetwork />

      {/* 背景光效 */}
      <Box
        sx={{
          position: 'fixed',
          top: '-30%',
          left: '-20%',
          width: '80%',
          height: '80%',
          background: `radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 60%)`,
          filter: 'blur(80px)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'fixed',
          bottom: '-30%',
          right: '-20%',
          width: '70%',
          height: '70%',
          background: `radial-gradient(ellipse, rgba(6, 182, 212, 0.08) 0%, transparent 60%)`,
          filter: 'blur(80px)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* 顶部导航栏 */}
      <AppBar
        position="relative"
        elevation={0}
        className="dashboard-appbar"
        sx={{
          background: COLORS.bgCard,
          borderBottom: `1px solid ${COLORS.border}`,
          zIndex: 10,
        }}
      >
        <Toolbar sx={{
          minHeight: { xs: 'auto', sm: 64 },
          padding: { xs: '6px 10px', sm: '0 16px' },
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 0.5, sm: 0 }
        }}>
          {/* PC端：全部在一行，Logo靠左，其他靠右 | 移动端：分两行 */}
          {isMobile ? (
            <>
              {/* 移动端第一行：Logo + 标题 + 个人中心 */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: isUltraNarrow ? 0.5 : 0.75 },
                  flexShrink: 1,
                  overflow: 'hidden'
                }}>
                  <Box className="logo-icon" sx={{
                    width: { xs: isUltraNarrow ? 28 : 32 },
                    height: { xs: isUltraNarrow ? 28 : 32 },
                    borderRadius: '6px',
                    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${COLORS.glow}`,
                    animation: 'logoPulse 3s ease-in-out infinite',
                    flexShrink: 0,
                    '@keyframes logoPulse': {
                      '0%, 100%': { boxShadow: `0 2px 8px ${COLORS.glow}` },
                      '50%': { boxShadow: `0 2px 16px ${COLORS.glow}, 0 0 20px ${COLORS.glow}` },
                    }
                  }}>
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: isUltraNarrow ? '0.7rem' : '0.8rem' } }}>KM</Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    component="h1"
                    className="site-title"
                    sx={{
                      fontSize: { xs: isUltraNarrow ? '0.9rem' : '1rem' },
                      whiteSpace: 'nowrap',
                      color: COLORS.text,
                      fontWeight: 700,
                      letterSpacing: '0.2px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    KM-AI智能写作
                  </Typography>
                </Box>
                <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.15, flexShrink: 0, ml: 0.5, '&:hover': { bgcolor: `${COLORS.primary}20` } }}>
                  <Avatar sx={{ width: { xs: isUltraNarrow ? 28 : 32 }, height: { xs: isUltraNarrow ? 28 : 32 }, background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`, border: '1.5px solid rgba(255,255,255,0.3)', boxShadow: `0 2px 8px ${COLORS.glow}` }}>
                    <AccountCircleIcon sx={{ fontSize: { xs: isUltraNarrow ? 16 : 18 }, color: '#fff' }} />
                  </Avatar>
                </IconButton>
              </Box>
              {/* 移动端第二行：余额 */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mt: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: isUltraNarrow ? 0.4 : 0.5 } }}>
                  <Box sx={{ width: { xs: isUltraNarrow ? 24 : 28 }, height: { xs: isUltraNarrow ? 24 : 28 }, borderRadius: '50%', background: 'rgba(255, 215, 0, 0.15)', border: '1.5px solid rgba(255, 215, 0, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MonetizationOnIcon sx={{ fontSize: isUltraNarrow ? 13 : 15, color: '#ffd700' }} />
                  </Box>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: { xs: isUltraNarrow ? '0.8rem' : '0.9rem' }, fontWeight: 700, color: '#ffd700', display: 'inline-flex', alignItems: 'baseline', gap: 0.25 }}>
                    {userInfo?.balance?.toLocaleString() || 0}
                    <Typography component="span" sx={{ fontSize: { xs: isUltraNarrow ? '0.7rem' : '0.75rem' }, color: 'rgba(255, 215, 0, 0.8)', fontWeight: 500 }}>字</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: isUltraNarrow ? 0.4 : 0.5 } }}>
                  <Box sx={{ width: { xs: isUltraNarrow ? 24 : 28 }, height: { xs: isUltraNarrow ? 24 : 28 }, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.08)', border: '1.5px solid rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditNoteIcon sx={{ fontSize: isUltraNarrow ? 13 : 15, color: 'rgba(255,255,255,0.7)' }} />
                  </Box>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: { xs: isUltraNarrow ? '0.8rem' : '0.9rem' }, fontWeight: 700, color: 'rgba(255, 255, 255, 0.85)', display: 'inline-flex', alignItems: 'baseline', gap: 0.25 }}>
                    {assistQuota.remaining}/{assistQuota.dailyLimit}
                    <Typography component="span" sx={{ fontSize: { xs: isUltraNarrow ? '0.7rem' : '0.75rem' }, color: 'rgba(255, 255, 255, 0.65)', fontWeight: 500 }}>次</Typography>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            /* PC端：一行，Logo靠左，余额+个人中心靠右 */
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {/* 左侧：Logo + 标题 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Box className="logo-icon" sx={{ width: 36, height: 36, borderRadius: '6px', background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${COLORS.glow}`, animation: 'logoPulse 3s ease-in-out infinite', flexShrink: 0, '@keyframes logoPulse': { '0%, 100%': { boxShadow: `0 2px 8px ${COLORS.glow}` }, '50%': { boxShadow: `0 2px 16px ${COLORS.glow}, 0 0 20px ${COLORS.glow}` } } }}>
                  <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem' }}>KM</Typography>
                </Box>
                <Typography variant="h5" component="h1" className="site-title" sx={{ fontSize: '1.2rem', whiteSpace: 'nowrap', color: COLORS.text, fontWeight: 700, letterSpacing: '0.2px' }}>
                  KM-AI智能写作
                </Typography>
              </Box>
              {/* 右侧：余额 + 个人中心 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* 字数余额 */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '20px',
                  background: 'rgba(255, 215, 0, 0.08)',
                  border: '1px solid rgba(255, 215, 0, 0.2)',
                }}>
                  <MonetizationOnIcon sx={{ fontSize: 16, color: '#ffd700' }} />
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600, color: '#ffd700' }}>
                    {userInfo?.balance?.toLocaleString() || 0}
                  </Box>
                  <Typography component="span" sx={{ fontSize: '0.7rem', color: 'rgba(255, 215, 0, 0.7)' }}>字</Typography>
                </Box>
                {/* 次数余额 */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '20px',
                  background: 'rgba(6, 182, 212, 0.08)',
                  border: '1px solid rgba(6, 182, 212, 0.2)',
                }}>
                  <EditNoteIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600, color: '#06b6d4' }}>
                    {assistQuota.remaining}/{assistQuota.dailyLimit}
                  </Box>
                  <Typography component="span" sx={{ fontSize: '0.7rem', color: 'rgba(6, 182, 212, 0.7)' }}>次</Typography>
                </Box>
                {/* 个人中心 */}
                <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.25, '&:hover': { bgcolor: `${COLORS.primary}20` } }}>
                  <Avatar sx={{ width: 32, height: 32, background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`, border: '1.5px solid rgba(255,255,255,0.2)', boxShadow: `0 2px 8px ${COLORS.glow}`, '&:hover': { boxShadow: `0 2px 16px ${COLORS.glow}`, transform: 'scale(1.05)' }, transition: 'all 0.3s ease' }}>
                    <AccountCircleIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Avatar>
                </IconButton>
              </Box>
            </Box>
          )}

          {/* 用户菜单 - 现代卡片样式 */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 200,
                bgcolor: COLORS.bgCard,
                backdropFilter: 'blur(32px)',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '16px',
                boxShadow: `-4px 4px 24px rgba(0, 0, 0, 0.4), 0 0 40px ${COLORS.glow}`,
                overflow: 'visible',
                mt: 1,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 16,
                  width: 12,
                  height: 12,
                  bgcolor: COLORS.bgCard,
                  borderTop: `1px solid ${COLORS.border}`,
                  borderLeft: `1px solid ${COLORS.border}`,
                  transform: 'translateY(-6px) rotate(45deg)'
                },
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  px: 2,
                  color: COLORS.text,
                  borderRadius: '8px',
                  mx: 1,
                  my: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: `${COLORS.primary}25`,
                    color: COLORS.accent
                  }
                }
              }
            }}
          >
            <MenuItem onClick={() => handleUserCenterOpen(0)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${COLORS.primary}40, ${COLORS.secondary}40)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <AccountCircleIcon sx={{ fontSize: 20, color: COLORS.accent }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>个人中心</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted }}>管理账户信息</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleUserCenterOpen(1)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: 'rgba(255, 215, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <MonetizationOnIcon sx={{ fontSize: 20, color: '#ffd700' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>充值中心</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted }}>快速充值字数</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => navigate('/feedback')} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: 'rgba(0, 180, 216, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <FeedbackIcon sx={{ fontSize: 20, color: '#00b4d8' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>我的反馈</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted }}>查看反馈记录</Typography>
              </Box>
            </MenuItem>
            <Divider sx={{ bgcolor: `${COLORS.border}`, my: 1 }} />
            <MenuItem onClick={handleLogout} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#ef4444 !important' }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <ExitToAppIcon sx={{ fontSize: 20, color: '#ef4444' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>退出登录</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(239, 68, 68, 0.7)' }}>退出当前账户</Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" className="main-container" sx={{ position: 'relative', zIndex: 3 }}>
        {/* 公众号引导横幅 */}
        <WechatGuideBanner />
        {announcement && (
          <Paper className="announcement-card" elevation={0} sx={{
            background: COLORS.bgCard,
            backdropFilter: 'blur(32px)',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            padding: { xs: 2, sm: 3 },
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '16px',
              padding: '1px',
              background: `linear-gradient(135deg, ${COLORS.primary}40, transparent 50%, ${COLORS.secondary}30)`,
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              MaskComposite: 'exclude',
              pointerEvents: 'none',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <CampaignIcon
                sx={{
                  mt: 0.5,
                  color: COLORS.primary,
                  fontSize: '2rem',
                  filter: `drop-shadow(0 0 10px ${COLORS.glow})`
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: '1.25rem',
                    color: COLORS.primary,
                    letterSpacing: '2px',
                    display: 'inline-block',
                    borderBottom: `2px solid ${COLORS.primary}50`,
                    paddingBottom: '4px'
                  }}
                >
                  系统公告
                </Typography>
                <Box
                  sx={{
                    color: announcement?.glowColor || COLORS.text,
                    lineHeight: 1.8,
                    fontSize: '1rem',
                    textShadow: resolveGlowShadow(announcement?.glowColor),
                    fontWeight: announcement?.glowColor ? 600 : 500,
                    transition: 'color 0.3s ease, text-shadow 0.3s ease',
                    '& p': {
                      margin: '0.5em 0'
                    },
                    '& h1, & h2, & h3': {
                      margin: '0.8em 0 0.5em 0',
                      fontWeight: 'bold'
                    },
                    '& ul, & ol': {
                      margin: '0.5em 0',
                      paddingLeft: '1.5em'
                    },
                    '& a': {
                      color: COLORS.accent,
                      textDecoration: 'underline'
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1
                    },
                    ...announcementGlowStyles
                  }}
                  dangerouslySetInnerHTML={{ __html: announcement.content }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 2,
                    display: 'block',
                    color: COLORS.textMuted,
                    fontSize: '0.75rem'
                  }}
                >
                  发布时间: {new Date(announcement.createdAt).toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        <Paper className="user-info-card" elevation={0} id="dashboard-welcome" sx={{
          background: COLORS.bgCard,
          backdropFilter: 'blur(32px)',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          padding: { xs: 2, sm: 3 },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '16px',
            padding: '1px',
            background: `linear-gradient(135deg, ${COLORS.secondary}40, transparent 50%, ${COLORS.primary}30)`,
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            MaskComposite: 'exclude',
            pointerEvents: 'none',
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                className="welcome-text"
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: COLORS.primary,
                    fontWeight: 700,
                    textShadow: `0 0 10px ${COLORS.glow}`
                  }}
                >
                  欢迎,
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: COLORS.secondary,
                    fontWeight: 800,
                    textShadow: `0 0 12px ${COLORS.glowGreen}`
                  }}
                >
                  {userInfo?.username || '用户'}
                </Box>
              </Typography>
            </Box>
          </Box>

          {/* 创作入口区域 */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: COLORS.text,
                fontWeight: 600,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <BoltIcon sx={{ color: COLORS.primary }} />
              选择创作模式
            </Typography>
            <Grid container spacing={2}>
              {/* 全自动生成小说入口 */}
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  onClick={() => navigate('/auto-novel')}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${COLORS.primary}25 0%, ${COLORS.primary}15 100%)`,
                    border: `2px solid ${COLORS.primary}50`,
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${COLORS.primary}35 0%, ${COLORS.primary}25 100%)`,
                      border: `2px solid ${COLORS.primary}80`,
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${COLORS.glow}`
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${COLORS.glow} 0%, transparent 70%)`
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 15px ${COLORS.glow}`
                      }}
                    >
                      <SmartToyIcon sx={{ fontSize: 32, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          color: COLORS.light,
                          textShadow: `0 0 10px ${COLORS.glow}`
                        }}
                      >
                        全自动生成
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                        AI全自动创作完整小说
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: COLORS.text, lineHeight: 1.6 }}>
                    输入小说主题，AI将自动生成<Box component="span" sx={{ color: COLORS.accent, fontWeight: 700 }}>大纲+正文</Box>，无需手动创作
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* 全自动创作标签 - 精致发光角标 */}
                    <Box
                      sx={{
                        position: 'relative',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        background: `linear-gradient(135deg, ${COLORS.accent}18 0%, ${COLORS.accent}08 100%)`,
                        border: `1px solid ${COLORS.accent}45`,
                        boxShadow: `0 0 10px ${COLORS.accent}20, inset 0 0 8px ${COLORS.accent}10`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '-1px',
                          left: '-1px',
                          width: 8,
                          height: 8,
                          borderTop: `1.5px solid ${COLORS.accent}`,
                          borderLeft: `1.5px solid ${COLORS.accent}`,
                          borderRadius: '3px 0 0 0'
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-1px',
                          right: '-1px',
                          width: 8,
                          height: 8,
                          borderBottom: `1.5px solid ${COLORS.accent}`,
                          borderRight: `1.5px solid ${COLORS.accent}`,
                          borderRadius: '0 0 3px 0'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: COLORS.accent,
                          boxShadow: `0 0 6px ${COLORS.accent}`,
                          animation: 'badgePulse 2s ease-in-out infinite'
                        }}
                      />
                      <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.accent, letterSpacing: '0.02em', lineHeight: 1 }}>
                        AI智能
                      </Box>
                    </Box>
                    {/* 项目数量角标 */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '6px',
                        background: `${COLORS.secondary}12`,
                        border: `1px solid ${COLORS.secondary}30`,
                        boxShadow: `0 0 8px ${COLORS.secondary}10`
                      }}
                    >
                      <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.secondary, fontFamily: 'monospace' }}>
                        {novels.filter((n) => n.creationType !== 'assist').length}
                      </Box>
                      <Box component="span" sx={{ fontSize: '0.65rem', color: `${COLORS.secondary}99`, lineHeight: 1 }}>
                        项目
                      </Box>
                    </Box>
                    {/* 加载/错误/重试状态 */}
                    {(loading || isRetrying || novelLoadError) && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.3,
                          borderRadius: '6px',
                          background: novelLoadError ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.08)',
                          border: `1px solid ${novelLoadError ? 'rgba(239,68,68,0.3)' : 'rgba(59,130,246,0.2)'}`,
                        }}
                      >
                        {loading || isRetrying ? (
                          <>
                            <CircularProgress size={10} sx={{ color: COLORS.primary }} />
                            <Box component="span" sx={{ fontSize: '0.65rem', color: COLORS.textMuted, lineHeight: 1 }}>
                              {isRetrying ? '重试中...' : '加载中...'}
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box component="span" sx={{ fontSize: '0.65rem', color: '#f87171', lineHeight: 1 }}>
                              {novelLoadError}
                            </Box>
                            <Box
                              component="span"
                              onClick={() => loadNovels()}
                              sx={{
                                fontSize: '0.65rem',
                                color: COLORS.primary,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                lineHeight: 1,
                                '&:hover': { color: COLORS.light }
                              }}
                            >
                              重试
                            </Box>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* 辅助生成小说入口 */}
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  onClick={() => navigate('/tools')}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${COLORS.accent}25 0%, ${COLORS.secondary}15 100%)`,
                    border: `2px solid ${COLORS.accent}50`,
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${COLORS.accent}35 0%, ${COLORS.secondary}25 100%)`,
                      border: `2px solid ${COLORS.accent}80`,
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${COLORS.glowGreen}`
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${COLORS.glowGreen} 0%, transparent 70%)`
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.secondary} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 15px ${COLORS.glowGreen}`
                      }}
                    >
                      <EditNoteIcon sx={{ fontSize: 32, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          color: COLORS.accent,
                          textShadow: `0 0 10px ${COLORS.glowGreen}`
                        }}
                      >
                        辅助生成
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                        手动创作，AI辅助润色
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: COLORS.text, lineHeight: 1.6 }}>
                    手动撰写小说内容，AI提供<Box component="span" sx={{ color: COLORS.primary, fontWeight: 700 }}>润色、改写、扩写</Box>等辅助功能
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* 辅助写作标签 */}
                    <Box
                      sx={{
                        position: 'relative',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        background: `linear-gradient(135deg, ${COLORS.primary}18 0%, ${COLORS.primary}08 100%)`,
                        border: `1px solid ${COLORS.primary}45`,
                        boxShadow: `0 0 10px ${COLORS.primary}20, inset 0 0 8px ${COLORS.primary}10`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '-1px',
                          left: '-1px',
                          width: 8,
                          height: 8,
                          borderTop: `1.5px solid ${COLORS.primary}`,
                          borderLeft: `1.5px solid ${COLORS.primary}`,
                          borderRadius: '3px 0 0 0'
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-1px',
                          right: '-1px',
                          width: 8,
                          height: 8,
                          borderBottom: `1.5px solid ${COLORS.primary}`,
                          borderRight: `1.5px solid ${COLORS.primary}`,
                          borderRadius: '0 0 3px 0'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: COLORS.primary,
                          boxShadow: `0 0 6px ${COLORS.primary}`,
                          animation: 'badgePulse 2s ease-in-out infinite'
                        }}
                      />
                      <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.primary, letterSpacing: '0.02em', lineHeight: 1 }}>
                        手动创作
                      </Box>
                    </Box>
                    {/* AI辅助标签 */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '6px',
                        background: `${COLORS.secondary}12`,
                        border: `1px solid ${COLORS.secondary}30`,
                        boxShadow: `0 0 8px ${COLORS.secondary}10`
                      }}
                    >
                      <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.secondary, letterSpacing: '0.02em', lineHeight: 1 }}>
                        AI辅助
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Typography
            variant="body2"
            className="user-description"
            sx={{
              color: COLORS.text,
              lineHeight: 1.7
            }}
          >
            邀请好友注册，好友
            <Box
              component="span"
              sx={{
                color: COLORS.primary,
                fontWeight: 700,
                mx: 0.5,
                textShadow: `0 0 8px ${COLORS.glow}`
              }}
            >
              每次充值
            </Box>
            时您可获得
            <Box
              component="span"
              sx={{
                color: COLORS.accent,
                fontWeight: 800,
                mx: 0.5,
                textShadow: `0 0 8px ${COLORS.glowGreen}`
              }}
            >
              10%
            </Box>
            奖励
          </Typography>
        </Paper>
      </Container>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: COLORS.bgCard,
            backdropFilter: 'blur(32px)',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            boxShadow: `0 0 100px ${COLORS.glow}, 0 32px 64px -12px rgba(0, 0, 0, 0.5)`,
          }
        }}
      >
        <DialogTitle sx={{ color: COLORS.accent, fontWeight: 'bold', borderBottom: `1px solid ${COLORS.border}` }}>
          创建新项目
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="项目名称"
            fullWidth
            value={newNovel.title}
            onChange={(e) => {
              // 限制 20 个字（不展示计数）
              const value = e.target.value.slice(0, 20);
              setNewNovel({ ...newNovel, title: value });
            }}
            placeholder="请填写项目名称（用于标识与检索，最多20字）"
            helperText="项目名称：用于在列表与编辑器中标识该项目，建议简洁清晰。"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: COLORS.text,
              },
              '& .MuiInputLabel-root': {
                color: COLORS.secondary
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${COLORS.secondary}50`
              },
              '& .MuiFormHelperText-root': {
                color: COLORS.textMuted
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.accent
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: COLORS.accent
              }
            }}
          />
          <TextField
            margin="dense"
            label="项目备注"
            fullWidth
            multiline
            rows={2}
            value={newNovel.description}
            onChange={(e) => {
              // 🔥 限制20个字
              const value = e.target.value.slice(0, 20);
              setNewNovel({ ...newNovel, description: value });
            }}
            // 不显示计数，仅提供用途说明
            helperText="备注：仅自己可见的补充说明，如用途、版本或阶段（最多20字）。"
            placeholder="例如：穿越小说、重生小说、测试稿、只是为了便于区分（最多20字）"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: COLORS.text,
              },
              '& .MuiInputLabel-root': {
                color: COLORS.secondary
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${COLORS.secondary}50`
              },
              '& .MuiFormHelperText-root': {
                color: COLORS.textMuted
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.accent
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: COLORS.accent
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{ color: COLORS.textMuted }}
          >
            取消
          </Button>
          <Button
            onClick={handleCreateNovel}
            variant="contained"
            disabled={!newNovel.title.trim()}
            sx={{
              background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.secondary})`,
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': {
                background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.accent})`,
              },
              '&.Mui-disabled': {
                background: `${COLORS.accent}30`,
                color: `${COLORS.text}50`
              }
            }}
          >
            创建
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 用户中心抽屉 */}
      <Drawer
        anchor="right"
        open={userCenterOpen}
        onClose={handleUserCenterClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '450px', md: '550px' },
            backgroundColor: COLORS.bgCard,
            backdropFilter: 'blur(32px)',
            border: `1px solid ${COLORS.border}`,
            boxShadow: `-5px 0 30px rgba(0, 0, 0, 0.5), 0 0 50px ${COLORS.glow}`
          }
        }}
      >
        <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
          <UserCenter 
            initialTab={userCenterInitialTab} 
            onClose={handleUserCenterClose}
          />
        </Box>
      </Drawer>

      {/* 通知 Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* 充值金额提示 Snackbar */}
      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
      >
        <Alert onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })} severity={snackbarInfo.severity}>
          {snackbarInfo.message}
        </Alert>
      </Snackbar>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: COLORS.bgCard, color: COLORS.accent, fontWeight: 'bold', borderBottom: `1px solid ${COLORS.border}` }}>
          确认删除
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 2, backgroundColor: COLORS.bgCard }}>
          <Typography sx={{ color: COLORS.text }}>
            确定删除这个小说吗？此操作无法撤销。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: COLORS.bgCard }}>
          <Button
            onClick={handleCloseDeleteDialog}
            color="inherit"
            sx={{ color: COLORS.textMuted }}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' }
            }}
            autoFocus
          >
            确定
          </Button>
        </DialogActions>
      </Dialog>

      <SessionExpiredDialog open={sessionExpiredDialogOpen} onClose={() => setSessionExpiredDialogOpen(false)} />
    </Box>
  );
};

export default Dashboard; 